import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Button, Col, Empty, message, Row, Tooltip } from "antd";
import { PaperClipOutlined, PlusOutlined, SendOutlined, LoadingOutlined } from "@ant-design/icons";
import { HugeRTEWrapper } from '../../components/HugeRTEWrapper';
import dayjs from "dayjs";
import styled from 'styled-components'

//import components
import MessageComponent from "../MessageComponent/MessageComponent";
import MessageTemplateButton from "./MessageTemplateButton";
import PhysicianAPI from '../../axios/physician/PhysicianApi'
import { useRequestWithDisclaimer } from "../../hooks/useRequestWithDisclaimer";
import { VeritasSpinner } from "../VeritasSpinner";

const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

const MessageListingComponent = ({
  roomId,
  sendMessage,
  messages,
  loading,
  allowImageUpload,
  handleImageUpload,
  markPatientChatAsRead,
  className,
  handleLoadMore,
  pageNumber,
  caseId,
  sendMessageLoading,
}) => {
  const [messageEntered, setMessageEntered] = useState("");
  const physicianDetails = useSelector(state => state.physicianDetails.physicianDetails) || {};
  const [caseDetails, setCaseDetails] = useState({});
  const [scrollHeight, setScrollHeight] = useState();
  const [loadedImageCount, setLoadedImageCount] = useState(0);
  const messageListingWrapperRef = useRef(null);

  const totalImagesToLoad = messages.messages.filter(message => !!message.key).length;
  const imgRefs = useRef(new Array(totalImagesToLoad));

  const handleImageLoad = useCallback(() => {
    setLoadedImageCount(count => count + 1);
  }, []);

  const handleMessageSendButtonClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    markPatientChatAsRead();
    if (messageEntered) {
      const data = {};
      data.chatRoomId = roomId;
      data.message = messageEntered;

      const handleSuccessMessageSent = () => {
        scrollToBottom();
        setMessageEntered("");
      };
      sendMessage(data, handleSuccessMessageSent)
    }
  };

  const handleMessageCreatedInDays = (value) => {
    if (value) {
      var createdDate = dayjs(
        dayjs(value).format("DD.MM.YYYY"),
        "DD.MM.YYYY"
      );
      var todaysDate = dayjs(dayjs().format("DD.MM.YYYY"), "DD.MM.YYYY");

      var result = todaysDate.diff(createdDate, "days");
      if (result === 0) {
        return "Today";
      } else if (result === 1) {
        return result +" day ago";
      } else return result +" days ago";
    } else {
      return "";
    }
  };
  const handleTextAreaChange = (value) => {
    setMessageEntered(value);
  };

  const handleAttachmentButtonClick = (event) => {
    event.stopPropagation();
    handleImageUpload();
  };
  const scrollToBottom = () => {
    messageListingWrapperRef.current.scrollTop =
      messageListingWrapperRef.current.scrollHeight;
    setScrollHeight(messageListingWrapperRef.current.scrollHeight);
  };
  const scrollToTopSection = () => {
    messageListingWrapperRef.current.scrollTop =
      messageListingWrapperRef.current.scrollHeight - scrollHeight;
    setScrollHeight(messageListingWrapperRef.current.scrollHeight);
  };

  const fetchCaseDetails = useRequestWithDisclaimer({
    initiateRequest: () => PhysicianAPI.getCaseDetailsById(caseId),
    onSuccess: (res) => setCaseDetails({ _id: caseId, ...res.data.payload }),
    onError: (err) => message.error('Unable to fetch case data'),
    checkNeedsDisclaimer: () => false
  })

  const onClickLoadMore = async () => {
    const isSuccess = await handleLoadMore();
    isSuccess && setTimeout(scrollToTopSection, 100);
  }

  useEffect(() => {
    if (!pageNumber || pageNumber === 1) {
      scrollToBottom();
      setLoadedImageCount(0);
    }
  }, [messages, pageNumber]);

  useEffect(() => {
    fetchCaseDetails();
  }, [])

  useEffect(() => {
    if (loadedImageCount === totalImagesToLoad) {
      scrollToBottom();
    }
  }, [loadedImageCount, totalImagesToLoad, scrollToBottom]);

  return (
    <>
      <div
        ref={messageListingWrapperRef}
        className={`message-list-messaging ${className}`}
      >
        { messages?.totalCount > messages.messages?.length && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              type="primary"
              size="large"
              style={{
                padding: "8px",
                display: "block",
                cursor: "pointer",
              }}
              onClick={onClickLoadMore}
            >
              <PlusOutlined /> Load More Messages
            </Button>
          </div>
        )}
        {loading ? (
          <div className="no-message-section">
            <VeritasSpinner size="large" />
          </div>
        ) : !!messages.messages?.length ? (
          <>
            {messages.messages.map((eachMessage, i) => (
              <MessageComponent
                ref={
                  el => {
                    if (!!eachMessage.key) {
                      imgRefs.current[i] = el
                      if (el && el.complete) {
                        handleImageLoad()
                      }
                    }
                  }
                }
                handleImageLoad={handleImageLoad}
                key={eachMessage._id}
                isRead={eachMessage.read}
                isPatient={eachMessage.isPatient}
                isAuthor={eachMessage.author === physicianDetails.userId}
                avatarUrl={messages.imageRefrence[eachMessage.author] || null}
                name={
                  eachMessage.author === physicianDetails.userId
                    ? "You"
                    : messages.nameRefrence[eachMessage.author]
                }
                time={
                  eachMessage.created
                    ? dayjs(eachMessage.created).format("LLL")
                    : dayjs().format("LLL")
                }
                messageSendInDays={handleMessageCreatedInDays(
                  eachMessage.created
                )}
                messageText={eachMessage.message}
                imageUrl={eachMessage.key || null}
                onClick={eachMessage.isPatient && !eachMessage.read ? markPatientChatAsRead : null}
              />
            ))}
          </>
        ) : (
          <div className="no-message-section">
            <Empty description="No messages yet" />
          </div>
        )}
      </div>
        <>
          <Row>
            <Col span={24}>
              <HugeRTEWrapper
                value={messageEntered}
                height={200}
                onChange={handleTextAreaChange}
                onFocus={markPatientChatAsRead}
                onKeyDown={event => {
                  if (!event.shiftKey && event.key === 'Enter') {
                    handleMessageSendButtonClick(event)
                  }
                }}
              />
            </Col>
          </Row>
          <Row>
            <ButtonGroup>
              <div>
                <MessageTemplateButton
                  caseDetails={caseDetails}
                  physicianDetails={physicianDetails}
                  setText={setMessageEntered}
                    />
                {allowImageUpload && (
                    <Tooltip placement="bottomLeft" title="Attach Image">
                      <Button
                        type="secondary"
                        className="chat-message-button"
                        onClick={handleAttachmentButtonClick}
                        icon={<PaperClipOutlined />}
                      >Attachment</Button>
                    </Tooltip>
                )}
              </div>
              <Tooltip placement="topLeft" title="Send Message">
                <Button
                  type="primary"
                  className="chat-message-button"
                  onClick={handleMessageSendButtonClick}
                  icon={sendMessageLoading ? <LoadingOutlined /> : <SendOutlined />}
                  disabled={!messageEntered || sendMessageLoading}
                >Send</Button>
              </Tooltip>
            </ButtonGroup>
          </Row>
        </>
    </>
  );
};
/*
*/

export default MessageListingComponent;
